import { useCreateGroupMutation, useAddGroupToCourseMutation } from '../../groups/groupsApi';
import { useCreateCourseMutation } from '../../course/courseApi';
import {
  useCreateSectionMutation,
  useCreatePageMutation,
  useCreatePageModuleMutation,
} from '../../editCourse/editCourseApi';
import { useAddUserToGroupMutation } from '../../users/usersApi';
import { useSendInvitationsMutation } from '../../invites/invitesApi';

// Template pages created inside each unit (section)
const UNIT_PAGES = [
  { name: 'Live Lessons', moduleType: 'webinar' },
  { name: 'Discussion', moduleType: 'discussion' },
  { name: 'Files', moduleType: 'download' },
  { name: 'Assignments', moduleType: 'task' },
];

export const useClassroomActions = () => {
  const [createGroup] = useCreateGroupMutation();
  const [addGroupToCourse] = useAddGroupToCourseMutation();
  const [createCourse] = useCreateCourseMutation();
  const [createSection] = useCreateSectionMutation();
  const [createPage] = useCreatePageMutation();
  const [createPageModule] = useCreatePageModuleMutation();
  const [addUserToGroup] = useAddUserToGroupMutation();
  const [sendInvitations] = useSendInvitationsMutation();

  const createClassroom = async ({ name, subjects = [], selectedStudents = [] }) => {
    // Step 1: Create the group (classroom)
    const groupResult = await createGroup(name).unwrap();
    const groupId = groupResult?.data?.id || groupResult?.id;

    // Step 2: Create the course
    const courseResult = await createCourse({ name: `${name}` }).unwrap();
    const courseId = courseResult?.data?.id || courseResult?.id;

    // Step 3: Link group to course
    if (groupId && courseId) {
      await addGroupToCourse({ circleId: groupId, courseIds: [courseId] }).unwrap();
    }

    // Step 4: Create sections, pages, and modules for each unit
    if (courseId && subjects.length > 0) {
      for (let i = 0; i < subjects.length; i++) {
        const sectionResult = await createSection({
          courseId,
          name: subjects[i],
          order: i,
        }).unwrap();

        const sectionId = sectionResult?.data?.id || sectionResult?.id;
        if (!sectionId) continue;

        // Create template pages with their modules inside each section
        for (let p = 0; p < UNIT_PAGES.length; p++) {
          const pageResult = await createPage({
            sectionId,
            courseId,
            name: UNIT_PAGES[p].name,
            order: p,
          }).unwrap();

          const pageId = pageResult?.data?.id || pageResult?.id;
          if (!pageId) continue;

          await createPageModule({
            type: UNIT_PAGES[p].moduleType,
            pageId,
            newRow: true,
          }).unwrap();
        }
      }
    }

    // Step 5: Add students to the group
    // Existing users (with id) are added directly, new emails are invited
    if (groupId && selectedStudents.length > 0) {
      const userIds = selectedStudents.map((s) => s.id).filter(Boolean);
      const emails = selectedStudents
        .map((s) => {
          const email = s.inputValue || (!s.id && (s.email || s));
          return email ? String(email).toLowerCase().trim() : null;
        })
        .filter(Boolean);

      if (userIds.length > 0) {
        await addUserToGroup({ userId: userIds, circleId: groupId }).unwrap();
      }
      if (emails.length > 0) {
        await sendInvitations({
          byWhat: 'emails',
          withWhat: emails,
          circleId: groupId,
          expires: '0000-00-00',
        }).unwrap();
      }
    }

    return { groupId, courseId };
  };

  return { createClassroom };
};
